/**
 * Landing page for the GitHub App install redirect (the app's `setup_url`).
 *
 * GitHub sends the user here after they install/update the app with
 * ?installation_id=…&setup_action=install|update. We don't trust those
 * params: the backend syncs installations from `GET /user/installations`
 * using the signed-in user's own token, so only installations the user can
 * actually see get linked.
 */
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../services/api';
import { useAuthStore } from '../stores/authStore';
import { _invalidateGithubRepoCache } from '../components/github/GithubRepoPicker';
import { consumeGithubReturnPath } from '../components/github/returnPath';

export function GithubSetupPage() {
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const [status, setStatus] = useState<'syncing' | 'done' | 'error'>('syncing');
  const [message, setMessage] = useState('Linking your GitHub repositories…');

  useEffect(() => {
    if (!user) {
      // Not signed in (e.g. installed the app before logging in). The
      // login-callback sync will pick the installation up afterwards.
      navigate('/login?from=github-setup', { replace: true });
      return;
    }
    (async () => {
      try {
        const { installations } = await api.syncGithubInstallations();
        _invalidateGithubRepoCache();
        const accounts = installations.map((i) => i.accountLogin).join(', ');
        setStatus('done');
        setMessage(
          installations.length > 0
            ? `Connected ${installations.length} installation${installations.length > 1 ? 's' : ''} (${accounts}). Redirecting…`
            : 'No installations found for your account yet. Redirecting…',
        );
        const returnTo = consumeGithubReturnPath() ?? '/dashboard';
        setTimeout(() => navigate(returnTo, { replace: true }), 1500);
      } catch (err) {
        setStatus('error');
        setMessage(err instanceof Error ? err.message : 'Failed to sync installations');
      }
    })();
  }, [user, navigate]);

  return (
    <div className="flex flex-col items-center justify-center min-h-[50vh] gap-4">
      <h1 className="text-xl font-semibold">GitHub repositories</h1>
      <p className={`text-sm ${status === 'error' ? 'text-destructive' : 'text-muted-foreground'}`}>
        {message}
      </p>
      {status === 'error' && (
        <button
          onClick={() => navigate('/dashboard')}
          className="px-4 py-2 text-sm rounded-md border border-border hover:bg-muted"
        >
          Back to dashboard
        </button>
      )}
    </div>
  );
}
